import { z } from 'zod';

import {
  ERROR_PORT,
  FLOW_LIMITS,
  FORBIDDEN_PATH_SEGMENTS,
  PORT_KEY_PATTERN,
} from '../constants/flow.constants';
import { PROMPT_VARIABLE_PATTERN, PromptMessagesSchema } from '../services/flow-prompt';
import { checkSchedule } from '../services/flow-schedule';
import { FlowConditionFormSchema } from './flow-condition.schema';

const id = z.string().trim().min(1).max(32);
const template = z.string().max(20_000);
const eventPath = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .refine(
    (path) => !path.split('.').some((segment) => FORBIDDEN_PATH_SEGMENTS.includes(segment)),
    'a path cannot use __proto__, constructor or prototype',
  );
const outputKey = z
  .string()
  .regex(PORT_KEY_PATTERN, 'lowercase letters, digits and _, up to 32')
  .refine((key) => key !== ERROR_PORT && key !== 'otherwise', 'this name is reserved');

const onError = {
  onError: z.enum(['stop', 'continue']).optional(),
  raiseIssue: z.boolean().optional(),
};

function uniqueKeys<T>(pick: (item: T) => string, what: string) {
  return (items: T[], ctx: z.RefinementCtx) => {
    const seen = new Set<string>();
    items.forEach((item, index) => {
      const key = pick(item);
      if (seen.has(key)) {
        ctx.addIssue({ code: 'custom', path: [index], message: `${what} "${key}" is used twice` });
      }
      seen.add(key);
    });
  };
}

export const WebhookTriggerConfigSchema = z.object({ sourceId: id });

export const ScheduleTriggerConfigSchema = z
  .object({
    cron: z.string().trim().min(6).max(120),
    timezone: z.string().trim().min(1).max(64).default('UTC'),
    overlap: z.enum(['skip', 'allow']).default('skip'),
    catchUp: z.enum(['one', 'none']).default('one'),
    input: z.record(z.string(), z.unknown()).optional(),
  })
  .superRefine((config, ctx) => {
    const check = checkSchedule(config.cron, config.timezone);
    if (!check.ok) {
      ctx.addIssue({ code: 'custom', path: [check.field ?? 'cron'], message: check.message! });
    }
  });

export const MANUAL_INPUT_KINDS = ['text', 'number', 'boolean', 'json'] as const;
export type TManualInputKind = (typeof MANUAL_INPUT_KINDS)[number];

export const ManualInputFieldSchema = z.object({
  name: z
    .string()
    .trim()
    .regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/, 'letters, digits and _, not starting with a digit'),
  kind: z.enum(MANUAL_INPUT_KINDS).default('text'),
  required: z.boolean().default(false),
  description: z.string().trim().max(500).optional(),
});
export type TManualInputField = z.infer<typeof ManualInputFieldSchema>;

// Without `inputFields` the Run now form is free JSON and any object is accepted.
export const ManualTriggerConfigSchema = z.object({
  sampleInput: z.record(z.string(), z.unknown()).optional(),
  inputFields: z
    .array(ManualInputFieldSchema)
    .max(50)
    .superRefine(uniqueKeys((item: { name: string }) => item.name, 'input field'))
    .optional(),
});

// A filter moved from a connection keeps its Filter-DSL (§11): the DSL's scope and strict types
// are not the form's, and behaving as before the move is the point. Editing replaces it.
export const LegacyFilterSchema = z.object({
  dsl: z.record(z.string(), z.unknown()),
  scope: z.enum(['body', 'headers', 'query', 'path']).default('body'),
});

export const FilterConfigSchema = z.union([
  z.object({ condition: FlowConditionFormSchema }),
  z.object({ legacy: LegacyFilterSchema }),
]);

export const DedupeConfigSchema = z.object({
  keyPaths: z.array(z.string().trim().min(1).max(200)).min(1).max(8),
  windowSec: z.number().int().positive().max(FLOW_LIMITS.maxDedupeWindowSec).default(3600),
  // Set by the import only: keys hashed the old connection's way, so its keys carry over.
  legacyScope: id.optional(),
});

export const RouteConfigSchema = z.object({
  routes: z
    .array(
      z.object({
        key: outputKey,
        name: z.string().trim().min(1).max(60),
        condition: FlowConditionFormSchema,
      }),
    )
    .min(1)
    .max(20)
    .superRefine(uniqueKeys((route: { key: string }) => route.key, 'route')),
});

export const SplitConfigSchema = z.object({
  path: z.string().trim().min(1).max(300),
  maxItems: z.number().int().positive().max(FLOW_LIMITS.maxSplitItems).default(1000),
});

const joinTimeout = z
  .number()
  .int()
  .positive()
  .max(FLOW_LIMITS.maxJoinTimeoutSec)
  .default(FLOW_LIMITS.defaultJoinTimeoutSec);

export const AggregateConfigSchema = z.object({ timeoutSec: joinTimeout });

export const MergeConfigSchema = z.object({
  inputs: z.number().int().min(1).max(FLOW_LIMITS.maxMergeInputs).default(2),
  mode: z.enum(['all', 'first', 'combine', 'collect']).default('all'),
  // Absent means `events`: what Combine did before `results` existed (§4.5).
  combines: z.enum(['events', 'results']).default('events'),
  timeoutSec: joinTimeout,
});

export const LoopConfigSchema = z.object({
  while: FlowConditionFormSchema,
  maxIterations: z
    .number()
    .int()
    .positive()
    .max(FLOW_LIMITS.maxLoopIterations)
    .default(FLOW_LIMITS.defaultLoopIterations),
});

export const WaitConfigSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('duration'),
    durationSec: z.number().int().positive().max(FLOW_LIMITS.maxWaitSec),
  }),
  // A target past maxWaitSec, in the past or absent leaves by `missing` (§5.1).
  z.object({
    mode: z.literal('until'),
    untilPath: z.string().trim().min(1).max(300),
    maxWaitSec: z
      .number()
      .int()
      .positive()
      .max(FLOW_LIMITS.maxWaitSec)
      .default(FLOW_LIMITS.defaultUntilWaitSec),
  }),
]);

// Exactly one of `source` and a pinned shared version (imported connections keep those, §5.1).
export const CodeConfigSchema = z
  .object({
    source: z.string().max(FLOW_LIMITS.maxCodeChars).optional(),
    transformId: id.optional(),
    transformVersionId: id.optional(),
    version: z.number().int().nonnegative().optional(),
    // `event` (§5.1): `(event, ctx) => newEvent`. `request`: the old transform's
    // `(request, context) => request`, which imported connections keep so they behave as before.
    signature: z.enum(['event', 'request']).default('event'),
    env: z.record(z.string(), z.string().max(4_000)).optional(),
    timeoutMs: z.number().int().positive().max(30_000).optional(),
    memoryMb: z.number().int().positive().max(512).optional(),
    ...onError,
  })
  .superRefine((config, ctx) => {
    const shared = config.transformVersionId !== undefined;
    if (config.source !== undefined && shared) {
      ctx.addIssue({
        code: 'custom',
        path: ['source'],
        message: 'either code in the step or shared code, not both',
      });
    } else if (config.source !== undefined) {
      if (config.source.trim() === '') {
        ctx.addIssue({ code: 'custom', path: ['source'], message: 'the code is empty' });
      }
    } else if (!shared) {
      ctx.addIssue({ code: 'custom', path: ['source'], message: 'write the code' });
    } else {
      for (const key of ['transformId', 'version'] as const) {
        if (config[key] === undefined) {
          ctx.addIssue({ code: 'custom', path: [key], message: 'pick the shared code version' });
        }
      }
    }
  });

export const SetConfigSchema = z.object({
  fields: z
    .array(z.object({ path: eventPath, value: template }))
    .min(1)
    .max(50),
  ...onError,
});

// No `ifNotFound` key (a pre-item-6 config): default set -> 'default', else 'error' (item 6).
export const LookupConfigSchema = z
  .object({
    value: template.min(1),
    target: eventPath,
    table: z
      .array(z.object({ from: z.string().max(500), to: z.string().max(2_000) }))
      .min(1)
      .max(500),
    ifNotFound: z.enum(['keep', 'default', 'error']).optional(),
    default: z.string().max(2_000).nullable().default(null),
    ...onError,
  })
  .transform((data) => ({
    ...data,
    ifNotFound: data.ifNotFound ?? (data.default !== null ? 'default' : 'error'),
  }));

export const DeliverConfigSchema = z
  .object({
    destinationId: id,
    sends: z.enum(['whole', 'field']).default('whole'),
    fieldPath: eventPath.optional(),
    ...onError,
  })
  .refine((v) => v.sends !== 'field' || v.fieldPath, {
    message: 'a field path is required when sending one field',
    path: ['fieldPath'],
  });

// The action's input schema belongs to its connector (§6); here it is only a record.
export const ActionConfigSchema = z.object({
  connector: z.string().trim().min(1).max(40),
  action: z.string().trim().min(1).max(60),
  connectorVersion: z.number().int().positive(),
  credentialId: id.nullable().default(null),
  input: z.record(z.string(), z.unknown()).default({}),
  ...onError,
});

export const LibraryPromptPinSchema = z.object({
  itemId: id,
  versionId: id,
  version: z.number().int().positive(),
  vars: z
    .record(z.string().regex(PROMPT_VARIABLE_PATTERN, 'a variable name'), template)
    .default({}),
});

const aiBase = {
  credentialId: id,
  model: z.string().trim().min(1).max(100).default('claude-sonnet-5'),
  messages: PromptMessagesSchema.optional(),
  library: LibraryPromptPinSchema.optional(),
  // Room for adaptive thinking, which counts against max_tokens: @hookpipe/connectors
  // DEFAULT_MAX_TOKENS, kept equal by hand because this file is shared with the web app.
  maxTokens: z.number().int().positive().max(64_000).default(8192),
  temperature: z.number().min(0).max(2).optional(),
  ...onError,
};

function onePrompt(config: { messages?: unknown; library?: unknown }, ctx: z.RefinementCtx): void {
  if (config.messages !== undefined && config.library !== undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['messages'],
      message: 'either messages in the step or a Library prompt, not both',
    });
  } else if (config.messages === undefined && config.library === undefined) {
    ctx.addIssue({ code: 'custom', path: ['messages'], message: 'write the prompt' });
  }
}

export const AiGenerateConfigSchema = z.object(aiBase).superRefine(onePrompt);

export const AiExtractConfigSchema = z
  .object({
    ...aiBase,
    fields: z
      .array(
        z.object({
          name: z.string().trim().min(1).max(60),
          type: z.enum(['text', 'number', 'boolean', 'list', 'object']),
          description: z.string().max(500).default(''),
        }),
      )
      .min(1)
      .max(50)
      .superRefine(uniqueKeys((field: { name: string }) => field.name, 'field')),
  })
  .superRefine(onePrompt);

export const AiClassifyConfigSchema = z
  .object({
    ...aiBase,
    categories: z
      .array(
        z.object({
          key: outputKey,
          name: z.string().trim().min(1).max(60),
          description: z.string().max(500).default(''),
        }),
      )
      .min(2)
      .max(20)
      .superRefine(uniqueKeys((category: { key: string }) => category.key, 'category')),
    fallbackCategory: outputKey.nullable().default(null),
  })
  .superRefine((config, ctx) => {
    onePrompt(config, ctx);
    const keys = config.categories.map((category) => category.key);
    if (config.fallbackCategory && !keys.includes(config.fallbackCategory)) {
      ctx.addIssue({
        code: 'custom',
        path: ['fallbackCategory'],
        message: 'the fallback must be one of the categories',
      });
    }
    if (config.onError === 'continue' && !config.fallbackCategory) {
      ctx.addIssue({
        code: 'custom',
        path: ['fallbackCategory'],
        message: 'to go on after a failure, pick the category used when unsure',
      });
    }
  });

export type TFilterConfig = z.output<typeof FilterConfigSchema>;
export type TRouteConfig = z.output<typeof RouteConfigSchema>;
export type TMergeConfig = z.output<typeof MergeConfigSchema>;
export type TLoopConfig = z.output<typeof LoopConfigSchema>;
export type TWaitConfig = z.output<typeof WaitConfigSchema>;
export type TAiClassifyConfig = z.output<typeof AiClassifyConfigSchema>;
export type TActionConfig = z.output<typeof ActionConfigSchema>;
export type TCodeConfig = z.output<typeof CodeConfigSchema>;
export type TLibraryPromptPin = z.output<typeof LibraryPromptPinSchema>;
